import { defineSemanticTokens } from '@chakra-ui/react'

export const colors = defineSemanticTokens.colors({
  bg: {
    DEFAULT: {
      value: {
        _light: '{colors.orange.100}',
        _dark: '{colors.purple.950}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.gray.100}',
        _dark: '{colors.purple.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.gray.200}',
        _dark: '{colors.purple.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.gray.300}',
        _dark: '{colors.purple.700}',
      },
    },
    inverted: {
      value: {
        _light: '{colors.black}',
        _dark: '{colors.white}',
      },
    },
    panel: {
      value: {
        _light: '{colors.white}',
        _dark: '{colors.black}',
      },
    },
    /**
     * Media stage (video call canvas, player backdrops). Intentionally dark in
     * both color modes — video content supplies its own light. Pair with
     * `fg.onStage` / `fg.onStageMuted` for text drawn on top.
     */
    stage: {
      value: {
        _light: '{colors.black}',
        _dark: '{colors.black}',
      },
    },
    container: {
      value: {
        _light: '{colors.white}',
        _dark: '{colors.black}',
      },
    },
    error: {
      value: {
        _light: '{colors.red.50}',
        _dark: '{colors.red.600}',
      },
    },
    warning: {
      value: {
        _light: '{colors.orange.50}',
        _dark: '{colors.orange.600}',
      },
    },
    success: {
      value: {
        _light: '{colors.green.50}',
        _dark: '{colors.green.600}',
      },
    },
    info: {
      value: {
        _light: '{colors.blue.50}',
        _dark: '{colors.blue.600}',
      },
    },
    lightest: {
      value: {
        _light: '{colors.whiteAlpha.100}',
        _dark: '{colors.whiteAlpha.50}',
      },
    },
    lighter: {
      value: {
        _light: '{colors.whiteAlpha.50}',
        _dark: '{colors.whiteAlpha.100}',
      },
    },
    darker: {
      value: {
        _light: '{colors.blackAlpha.50}',
        _dark: '{colors.blackAlpha.100}',
      },
    },
    darkest: {
      value: {
        _light: '{colors.blackAlpha.100}',
        _dark: '{colors.blackAlpha.200}',
      },
    },
  },
  fg: {
    DEFAULT: {
      value: {
        _light: '{colors.black}',
        _dark: '{colors.gray.50}',
      },
    },
    muted: {
      value: {
        _light: '{colors.gray.600}',
        _dark: '{colors.gray.400}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.gray.400}',
        _dark: '{colors.gray.500}',
      },
    },
    /** Text on `bg.stage` — fixed light in both modes. */
    onStage: {
      value: {
        _light: '{colors.whiteAlpha.900}',
        _dark: '{colors.whiteAlpha.900}',
      },
    },
    onStageMuted: {
      value: {
        _light: '{colors.whiteAlpha.700}',
        _dark: '{colors.whiteAlpha.700}',
      },
    },
    inverted: {
      value: {
        _light: '{colors.gray.50}',
        _dark: '{colors.black}',
      },
    },
    error: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.400}',
      },
    },
    warning: {
      value: {
        _light: '{colors.orange.600}',
        _dark: '{colors.orange.300}',
      },
    },
    success: {
      value: {
        _light: '{colors.green.600}',
        _dark: '{colors.green.300}',
      },
    },
    info: {
      value: {
        _light: '{colors.blue.600}',
        _dark: '{colors.blue.300}',
      },
    },
  },
  border: {
    DEFAULT: {
      value: {
        _light: '{colors.gray.200}',
        _dark: '{colors.gray.800}',
      },
    },
    muted: {
      value: {
        _light: '{colors.gray.100}',
        _dark: '{colors.gray.900}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.gray.50}',
        _dark: '{colors.gray.950}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.gray.300}',
        _dark: '{colors.gray.700}',
      },
    },
    inverted: {
      value: {
        _light: '{colors.gray.800}',
        _dark: '{colors.gray.200}',
      },
    },
    error: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.400}',
      },
    },
    warning: {
      value: {
        _light: '{colors.orange.500}',
        _dark: '{colors.orange.400}',
      },
    },
    success: {
      value: {
        _light: '{colors.green.500}',
        _dark: '{colors.green.400}',
      },
    },
    info: {
      value: {
        _light: '{colors.blue.500}',
        _dark: '{colors.blue.400}',
      },
    },
  },
  gray: {
    bg: {
      value: {
        _light: '{colors.gray.200}',
        _dark: '{colors.gray.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.gray.100}',
        _dark: '{colors.gray.900}',
      },
    },
    container: {
      value: {
        _light: '{colors.gray.50}',
        _dark: '{colors.gray.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.gray.700}',
      },
    },
    contrast: {
      value: {
        _light: '{colors.black}',
        _dark: '{colors.white}',
      },
    },
    fg: {
      value: {
        _light: '{colors.gray.800}',
        _dark: '{colors.gray.100}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.gray.100}',
        _dark: '{colors.gray.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.gray.200}',
        _dark: '{colors.gray.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.gray.300}',
        _dark: '{colors.gray.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.gray.600}',
        _dark: '{colors.gray.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.gray.400}',
        _dark: '{colors.gray.400}',
      },
    },
    border: {
      value: {
        _light: '{colors.gray.200}',
        _dark: '{colors.gray.800}',
      },
    },
  },
  red: {
    bg: {
      value: {
        _light: '{colors.red.200}',
        _dark: '{colors.red.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.red.100}',
        _dark: '{colors.red.800}',
      },
    },
    container: {
      value: {
        _light: '{colors.red.50}',
        _dark: '{colors.red.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.red.700}',
      },
    },
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.red.700}',
        _dark: '{colors.red.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.red.100}',
        _dark: '{colors.red.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.red.200}',
        _dark: '{colors.red.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.red.300}',
        _dark: '{colors.red.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.red.600}',
        _dark: '{colors.red.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.400}',
      },
    },
  },
  orange: {
    bg: {
      value: {
        _light: '{colors.orange.100}',
        _dark: '{colors.orange.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.orange.50}',
        _dark: '{colors.orange.900}',
      },
    },
    container: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.orange.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.white}',
        _dark: '{colors.orange.700}',
      },
    },
    contrast: {
      value: {
        _light: 'white',
        _dark: 'black',
      },
    },
    fg: {
      value: {
        _light: '{colors.orange.800}',
        _dark: '{colors.orange.200}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.orange.200}',
        _dark: '{colors.orange.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.orange.100}',
        _dark: '{colors.orange.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.orange.50}',
        _dark: '{colors.orange.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.orange.600}',
        _dark: '{colors.orange.500}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.orange.500}',
        _dark: '{colors.orange.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.orange.700/50}',
        _dark: '{colors.orange.400}',
      },
    },
  },
  green: {
    bg: {
      value: {
        _light: '{colors.green.200}',
        _dark: '{colors.green.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.green.100}',
        _dark: '{colors.green.800}',
      },
    },
    container: {
      value: {
        _light: '{colors.green.50}',
        _dark: '{colors.green.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.green.700}',
      },
    },
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.green.700}',
        _dark: '{colors.green.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.green.100}',
        _dark: '{colors.green.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.green.200}',
        _dark: '{colors.green.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.green.300}',
        _dark: '{colors.green.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.green.600}',
        _dark: '{colors.green.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.green.500}',
        _dark: '{colors.green.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.green.500}',
        _dark: '{colors.green.400}',
      },
    },
  },
  blue: {
    bg: {
      value: {
        _light: '{colors.blue.200}',
        _dark: '{colors.blue.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.blue.100}',
        _dark: '{colors.blue.800}',
      },
    },
    container: {
      value: {
        _light: '{colors.blue.50}',
        _dark: '{colors.blue.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.blue.700}',
      },
    },
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.blue.700}',
        _dark: '{colors.blue.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.blue.100}',
        _dark: '{colors.blue.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.blue.200}',
        _dark: '{colors.blue.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.blue.300}',
        _dark: '{colors.blue.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.blue.600}',
        _dark: '{colors.blue.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.blue.500}',
        _dark: '{colors.blue.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.blue.500}',
        _dark: '{colors.blue.400}',
      },
    },
  },
  yellow: {
    bg: {
      value: {
        _light: '{colors.yellow.200}',
        _dark: '{colors.yellow.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.yellow.100}',
        _dark: '{colors.yellow.800}',
      },
    },
    container: {
      value: {
        _light: '{colors.yellow.50}',
        _dark: '{colors.yellow.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.yellow.700}',
      },
    },
    contrast: {
      value: {
        _light: 'black',
        _dark: 'black',
      },
    },
    fg: {
      value: {
        _light: '{colors.yellow.800}',
        _dark: '{colors.yellow.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.yellow.100}',
        _dark: '{colors.yellow.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.yellow.200}',
        _dark: '{colors.yellow.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.yellow.500}',
        _dark: '{colors.yellow.500}',
      },
    },
    solid: {
      value: {
        _light: '{colors.yellow.300}',
        _dark: '{colors.yellow.300}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.yellow.500}',
        _dark: '{colors.yellow.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.yellow.500}',
        _dark: '{colors.yellow.500}',
      },
    },
  },
  teal: {
    bg: {
      value: {
        _light: '{colors.teal.200}',
        _dark: '{colors.teal.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.teal.100}',
        _dark: '{colors.teal.800}',
      },
    },
    container: {
      value: {
        _light: '{colors.teal.50}',
        _dark: '{colors.teal.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.teal.700}',
      },
    },
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.teal.700}',
        _dark: '{colors.teal.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.teal.100}',
        _dark: '{colors.teal.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.teal.200}',
        _dark: '{colors.teal.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.teal.300}',
        _dark: '{colors.teal.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.teal.600}',
        _dark: '{colors.teal.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.teal.500}',
        _dark: '{colors.teal.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.teal.500}',
        _dark: '{colors.teal.400}',
      },
    },
  },
  purple: {
    bg: {
      value: {
        _light: '{colors.purple.200}',
        _dark: '{colors.purple.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.purple.100}',
        _dark: '{colors.purple.900}',
      },
    },
    container: {
      value: {
        _light: '{colors.purple.50}',
        _dark: '{colors.purple.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.purple.700}',
      },
    },
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.purple.900}',
        _dark: '{colors.purple.100}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.purple.100}',
        _dark: '{colors.purple.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.purple.200}',
        _dark: '{colors.purple.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.purple.300}',
        _dark: '{colors.purple.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.purple.600}',
        _dark: '{colors.purple.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.purple.500}',
        _dark: '{colors.purple.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.purple.300}',
        _dark: '{colors.purple.700}',
      },
    },
  },
  pink: {
    bg: {
      value: {
        _light: '{colors.pink.200}',
        _dark: '{colors.pink.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.pink.100}',
        _dark: '{colors.pink.800}',
      },
    },
    container: {
      value: {
        _light: '{colors.pink.50}',
        _dark: '{colors.pink.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.pink.700}',
      },
    },
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.pink.700}',
        _dark: '{colors.pink.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.pink.100}',
        _dark: '{colors.pink.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.pink.200}',
        _dark: '{colors.pink.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.pink.300}',
        _dark: '{colors.pink.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.pink.600}',
        _dark: '{colors.pink.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.pink.500}',
        _dark: '{colors.pink.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.pink.500}',
        _dark: '{colors.pink.400}',
      },
    },
  },
  cyan: {
    bg: {
      value: {
        _light: '{colors.cyan.200}',
        _dark: '{colors.cyan.950}',
      },
    },
    panel: {
      value: {
        _light: '{colors.cyan.100}',
        _dark: '{colors.cyan.800}',
      },
    },
    container: {
      value: {
        _light: '{colors.cyan.50}',
        _dark: '{colors.cyan.800}',
      },
    },
    card: {
      value: {
        _light: '{colors.whiteAlpha.500}',
        _dark: '{colors.cyan.700}',
      },
    },
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.cyan.700}',
        _dark: '{colors.cyan.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.cyan.100}',
        _dark: '{colors.cyan.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.cyan.200}',
        _dark: '{colors.cyan.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.cyan.300}',
        _dark: '{colors.cyan.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.cyan.600}',
        _dark: '{colors.cyan.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.cyan.500}',
        _dark: '{colors.cyan.500}',
      },
    },
    border: {
      value: {
        _light: '{colors.cyan.500}',
        _dark: '{colors.cyan.400}',
      },
    },
  },
  // Admin area semantic tokens — color-mode adaptive (used by filter bars, tables, expanded rows, etc.)
  admin: {
    bg: {
      DEFAULT: {
        value: {
          _light: '{colors.gray.50}',
          _dark: '{colors.gray.900}',
        },
      },
      panel: {
        value: {
          _light: '{colors.white}',
          _dark: '{colors.blackAlpha.700}',
        },
      },
      hover: {
        value: {
          _light: '{colors.blackAlpha.50}',
          _dark: '{colors.whiteAlpha.100}',
        },
      },
      muted: {
        value: {
          _light: '{colors.gray.100}',
          _dark: '{colors.blackAlpha.500}',
        },
      },
      toolbar: {
        value: {
          _light: '{colors.gray.100}',
          _dark: '{colors.blackAlpha.300}',
        },
      },
      // Inputs / selects sitting on panel chrome
      input: {
        value: {
          _light: '{colors.white}',
          _dark: '{colors.blackAlpha.400}',
        },
      },
    },
    border: {
      DEFAULT: {
        value: {
          _light: '{colors.gray.200}',
          _dark: '{colors.whiteAlpha.100}',
        },
      },
    },
    fg: {
      muted: {
        value: {
          _light: '{colors.gray.600}',
          _dark: '{colors.whiteAlpha.500}',
        },
      },
      subtle: {
        value: {
          _light: '{colors.gray.500}',
          _dark: '{colors.whiteAlpha.600}',
        },
      },
    },
    accent: {
      DEFAULT: {
        value: {
          _light: '{colors.purple.600}',
          _dark: '{colors.purple.800}',
        },
      },
      muted: {
        value: {
          _light: '{colors.purple.500}',
          _dark: '{colors.purple.700}',
        },
      },
    },
  },
})
